import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Trash2, Loader2, ShieldCheck } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';
import { useJurisdictions } from '@/hooks/useJurisdictions';
import type { OrgEntitlement } from '@/types/database';

interface OrgEntitlementsTabProps {
  orgId: string;
  orgName: string;
}

async function fetchEntitlements(orgId: string): Promise<OrgEntitlement[]> {
  const res = await apiFetch(`/api/entitlements?org_id=${orgId}`);
  if (!res.ok) throw new Error(`Failed to load entitlements: ${res.status}`);
  const json = await res.json();
  return (json.data ?? []) as OrgEntitlement[];
}

interface GrantFormData {
  jurisdiction_id: string;
  status: 'active' | 'lapsed';
  valid_from: string;
  valid_until: string;
}

const EMPTY_GRANT: GrantFormData = {
  jurisdiction_id: '',
  status: 'active',
  valid_from: new Date().toISOString().slice(0, 10),
  valid_until: '',
};

export const OrgEntitlementsTab: React.FC<OrgEntitlementsTabProps> = ({
  orgId,
  orgName,
}) => {
  const [entitlements, setEntitlements] = useState<OrgEntitlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const [form, setForm] = useState<GrantFormData>(EMPTY_GRANT);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { flat: jurisdictions } = useJurisdictions();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEntitlements(orgId);
      setEntitlements(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleOpenGrant = (next: boolean) => {
    if (next) {
      setForm(EMPTY_GRANT);
      setFormError(null);
    }
    setGrantOpen(next);
  };

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.jurisdiction_id) {
      setFormError('Jurisdiction is required');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const res = await apiFetch('/api/entitlements', {
        method: 'POST',
        body: JSON.stringify({
          org_id: orgId,
          jurisdiction_id: form.jurisdiction_id,
          status: form.status,
          valid_from: form.valid_from || new Date().toISOString(),
          valid_until: form.valid_until || null,
        }),
      });
      if (!res.ok) {
        const eBody = await res.json().catch(() => ({}));
        throw new Error(
          (eBody as { error?: string }).error ?? 'Failed to grant entitlement'
        );
      }
      setGrantOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to grant');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (id: string) => {
    await apiFetch(`/api/entitlements/${id}`, { method: 'DELETE' });
    setEntitlements((prev) => prev.filter((e) => e.id !== id));
  };

  const handleSetStatus = async (id: string, status: 'active' | 'lapsed') => {
    await apiFetch(`/api/entitlements/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    setEntitlements((prev) =>
      prev.map((e) => (e.id === id ? { ...e, status } : e))
    );
  };

  const isLapsed = (e: OrgEntitlement) =>
    e.status !== 'active' ||
    (!!e.valid_until && new Date(e.valid_until) < new Date());

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              Entitlements
            </CardTitle>
            <CardDescription>
              Jurisdiction data access grants for {orgName}.
            </CardDescription>
          </div>
          <Dialog open={grantOpen} onOpenChange={handleOpenGrant}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Grant Access
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Grant Jurisdiction Access</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleGrant} className="space-y-4 py-2">
                <div className="space-y-1">
                  <Label>Jurisdiction *</Label>
                  <Select
                    value={form.jurisdiction_id}
                    onValueChange={(v) =>
                      setForm({ ...form, jurisdiction_id: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select jurisdiction…" />
                    </SelectTrigger>
                    <SelectContent>
                      {jurisdictions.map((j) => (
                        <SelectItem key={j.id} value={j.id}>
                          {j.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Granting a node also grants access to all its descendants.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Status</Label>
                    <Select
                      value={form.status}
                      onValueChange={(v) =>
                        setForm({
                          ...form,
                          status: v as 'active' | 'lapsed',
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="lapsed">Lapsed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Valid from</Label>
                    <Input
                      type="date"
                      value={form.valid_from}
                      onChange={(e) =>
                        setForm({ ...form, valid_from: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>
                    Valid until{' '}
                    <span className="font-normal text-gray-400">
                      (optional)
                    </span>
                  </Label>
                  <Input
                    type="date"
                    value={form.valid_until}
                    onChange={(e) =>
                      setForm({ ...form, valid_until: e.target.value })
                    }
                  />
                  <p className="text-xs text-gray-500">
                    Leave blank for no expiry.
                  </p>
                </div>
                {formError && (
                  <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                    {formError}
                  </p>
                )}
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setGrantOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    Grant
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {error && (
          <div className="border-b border-red-200 bg-red-50 px-6 py-3 text-sm text-red-600">
            {error}
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading entitlements…
          </div>
        ) : entitlements.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No entitlements for this organization.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Jurisdiction</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Valid from</TableHead>
                <TableHead>Valid until</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {entitlements.map((e) => {
                const jur = (
                  e as OrgEntitlement & {
                    jurisdictions?: { name: string; level: string };
                  }
                ).jurisdictions;
                const lapsed = isLapsed(e);

                return (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">
                      {jur ? (
                        <span>
                          {jur.name}
                          <Badge variant="outline" className="ml-2 text-[10px]">
                            {jur.level}
                          </Badge>
                        </span>
                      ) : (
                        <span className="font-mono text-xs text-gray-400">
                          {e.jurisdiction_id}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={lapsed ? 'destructive' : 'default'}
                        className="cursor-pointer"
                        onClick={() =>
                          handleSetStatus(e.id, lapsed ? 'active' : 'lapsed')
                        }
                        title="Click to toggle"
                      >
                        {lapsed ? 'Lapsed' : 'Active'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {e.valid_from
                        ? new Date(e.valid_from).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {e.valid_until
                        ? new Date(e.valid_until).toLocaleDateString()
                        : '∞'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-400 hover:text-red-600"
                        title="Revoke"
                        onClick={() => handleRevoke(e.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
